<template>
  <b-modal ref="modal" :title="msg('Edit Contract')" size="xl" hide-footer scrollable
           modal-class="contract-editor-modal" @hidden="onHidden">
    <template v-if="contract">
      <b-tabs content-class="mt-1" nav-class="contract-tabs">
        <!-- Details Tab -->
        <b-tab :title="msg('Details')" active>
          <b-row>
            <!-- Client Info -->
            <b-col cols="12">
              <h6 class="section-label mb-75">
                <feather-icon icon="UserIcon" size="14" class="mr-50"/>{{ msg('Client Information') }}
              </h6>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Company Name')">
                <b-form-input v-model="form.clientCompanyName" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Contact Name')">
                <b-form-input v-model="form.clientName" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Email')">
                <b-form-input v-model="form.clientEmail" type="email" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Phone')">
                <b-form-input v-model="form.clientPhone" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="msg('Address')">
                <b-form-input v-model="form.clientAddress" size="sm"/>
              </b-form-group>
            </b-col>

            <!-- Financial Terms -->
            <b-col cols="12" class="mt-1">
              <h6 class="section-label mb-75">
                <feather-icon icon="DollarSignIcon" size="14" class="mr-50"/>{{ msg('Financial Terms') }}
              </h6>
            </b-col>
            <b-col cols="12" md="4" lg="3">
              <b-form-group :label="msg('Setup Fee (€)')">
                <b-form-input v-model.number="form.setupFee" type="number" step="0.01" min="0" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" lg="3">
              <b-form-group :label="msg('Management Fee Mode')">
                <b-form-select v-model="form.managementFeeMode" :options="feeModeOptions" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col v-if="form.managementFeeMode === 'PERCENTAGE'" cols="12" md="4" lg="3">
              <b-form-group :label="msg('Management Fee (%)')">
                <b-form-input v-model.number="form.managementFeePercent" type="number" step="0.5" min="0" max="100" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col v-if="form.managementFeeMode === 'PER_UNIT'" cols="12" md="4" lg="3">
              <b-form-group :label="msg('Unit Price (€)')">
                <b-form-input v-model.number="form.unitPrice" type="number" step="0.5" min="0" size="sm"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" lg="3">
              <b-form-group :label="msg('Keybox Price (€)')">
                <b-form-input v-model.number="form.keyboxPrice" type="number" step="0.01" min="0" size="sm"/>
              </b-form-group>
            </b-col>

            <!-- Language -->
            <b-col cols="12" md="4" lg="3">
              <b-form-group :label="msg('Language')">
                <b-form-select v-model="form.language" :options="languageOptions" size="sm"/>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="mt-1 d-flex flex-wrap" style="gap:8px">
            <b-button variant="primary" size="sm" @click="saveDetails" :disabled="saving">
              <feather-icon icon="SaveIcon" size="14" class="mr-25"/>{{ msg('Save') }}
            </b-button>
            <b-button v-if="contract.status === 'DRAFT'" variant="info" size="sm" @click="sendByEmail" :disabled="saving">
              <feather-icon icon="SendIcon" size="14" class="mr-25"/>{{ msg('Send by Email') }}
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="previewPdf" :disabled="saving">
              <feather-icon icon="EyeIcon" size="14" class="mr-25"/>{{ msg('Preview PDF') }}
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="downloadPdf" :disabled="saving">
              <feather-icon icon="DownloadIcon" size="14" class="mr-25"/>{{ msg('Download PDF') }}
            </b-button>
          </div>
        </b-tab>

        <!-- Contract Text Tab -->
        <b-tab :title="msg('Contract Text')" @click="initEditor">
          <div class="contract-editor-wrapper">
            <div class="contract-editor-toolbar mb-50">
              <small class="text-muted">{{ msg('Edit the contract HTML content below. Formatting will be preserved in the generated PDF.') }}</small>
            </div>
            <div
                ref="editor"
                class="contract-editor-area"
                contenteditable="true"
                @input="onEditorInput"
            />
          </div>
          <div class="mt-1 d-flex flex-wrap" style="gap:8px">
            <b-button variant="primary" size="sm" @click="saveHtmlBody" :disabled="saving">
              <feather-icon icon="SaveIcon" size="14" class="mr-25"/>{{ msg('Save') }}
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="previewPdf" :disabled="saving">
              <feather-icon icon="EyeIcon" size="14" class="mr-25"/>{{ msg('Preview PDF') }}
            </b-button>
          </div>
        </b-tab>
      </b-tabs>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'ContractEditorModal',
  data() {
    return {
      contract: null,
      form: {},
      saving: false,
      editorInitialized: false,
    }
  },
  computed: {
    feeModeOptions() {
      return [
        { value: 'PERCENTAGE', text: this.msg('Percentage') },
        { value: 'PER_UNIT', text: this.msg('Per Unit') },
      ]
    },
    languageOptions() {
      return [
        { value: 'EN', text: 'English' },
        { value: 'DE', text: 'Deutsch' },
      ]
    },
  },
  methods: {
    open(contract) {
      this.contract = contract
      this.editorInitialized = false
      this.form = {
        clientCompanyName: contract.clientCompanyName,
        clientName: contract.clientName,
        clientEmail: contract.clientEmail,
        clientPhone: contract.clientPhone,
        clientAddress: contract.clientAddress,
        setupFee: contract.setupFee,
        managementFeeMode: contract.managementFeeMode,
        managementFeePercent: contract.managementFeePercent,
        unitPrice: contract.unitPrice,
        keyboxPrice: contract.keyboxPrice,
        language: contract.language,
        htmlBody: contract.htmlBody,
      }
      this.$refs.modal.show()
    },
    onHidden() {
      this.contract = null
      this.form = {}
      this.editorInitialized = false
    },
    initEditor() {
      this.$nextTick(() => {
        if (this.$refs.editor && !this.editorInitialized) {
          this.$refs.editor.innerHTML = this.form.htmlBody || ''
          this.editorInitialized = true
        }
      })
    },
    onEditorInput() {
      if (this.$refs.editor) {
        this.form.htmlBody = this.$refs.editor.innerHTML
      }
    },
    async saveDetails() {
      this.saving = true
      try {
        const payload = { ...this.form }
        delete payload.htmlBody
        const ans = await this.$store.dispatch('contract/updateContract', { id: this.contract.id, contract: payload })
        if (ans && ans.Contract) {
          this.contract = ans.Contract
        }
        this.$emit('saved')
      } finally {
        this.saving = false
      }
    },
    async saveHtmlBody() {
      this.saving = true
      try {
        const ans = await this.$store.dispatch('contract/updateContract', {
          id: this.contract.id,
          contract: { htmlBody: this.form.htmlBody }
        })
        if (ans && ans.Contract) {
          this.contract = ans.Contract
        }
        this.$emit('saved')
      } finally {
        this.saving = false
      }
    },
    async sendByEmail() {
      const ok = await this.$bvModal.msgBoxConfirm(
          this.msg('Send this contract by email to') + ' ' + (this.form.clientEmail || '?') + '?',
          { title: this.msg('Confirm Send'), okVariant: 'primary', okTitle: this.msg('Send'), cancelTitle: this.msg('Cancel') }
      )
      if (!ok) return
      this.saving = true
      try {
        await this.$store.dispatch('contract/sendContract', { id: this.contract.id })
        this.contract.status = 'SENT'
        this.$emit('sent')
      } finally {
        this.saving = false
      }
    },
    previewPdf() {
      const url = window.config.apiUrl + 'contracts/download/' + this.contract.id
      const headers = { 'Authorization': 'Bearer ' + localStorage.Bearer }
      fetch(url, { headers })
          .then(res => res.blob())
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob)
            window.open(blobUrl, '_blank')
          })
    },
    downloadPdf() {
      this.$store.dispatch('contract/downloadPdf', { id: this.contract.id })
    },
  }
}
</script>

<style lang="scss" scoped>
.section-label {
  font-weight: 600; color: #4f46e5; font-size: .85rem;
  border-bottom: 1px solid #eee; padding-bottom: 6px;
}
.dark-layout .section-label {
  color: #a78bfa; border-bottom-color: #333;
}

.contract-editor-wrapper {
  border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;
}
.dark-layout .contract-editor-wrapper {
  border-color: #555; box-shadow: 0 2px 8px rgba(0,0,0,.3);
}
.contract-editor-toolbar {
  padding: 8px 12px; background: #f9fafb; border-bottom: 1px solid #e0e0e0;
}
.dark-layout .contract-editor-toolbar {
  background: #f0f0f0; border-bottom-color: #ddd; 
  .text-muted { color: #666 !important; }
}
.contract-editor-area {
  min-height: 500px; padding: 20px; font-size: 11pt; line-height: 1.6; outline: none;
  background: #fff; color: #222; overflow-y: auto; max-height: 65vh;
}
.dark-layout .contract-editor-area {
  background: #fff; color: #222;
}
</style>

<style lang="scss">
.dark-layout .contract-editor-area {
  h1, h2, h3, h4, h5, h6,
  p, span, div, li, ul, ol,
  td, th, tr, table,
  strong, b, em, i, a,
  label, small {
    color: inherit !important;
  }
  h2 {
    color: #4f46e5 !important;
  }
  .company-name {
    color: #4f46e5 !important;
  }
  .company-info, .party-details, .party-label {
    color: #666 !important;
  }
  .contract-title {
    color: #1e1b4b !important;
  }
  .contract-number {
    color: #888 !important;
  }
  .section-body, .section-body p {
    color: #333 !important;
  }
  .fee-table th {
    background: #f5f3ff !important;
    color: #4f46e5 !important;
  }
  .fee-table td {
    color: #222 !important;
  }
  .sig-name {
    color: #222 !important;
  }
  .sig-line {
    color: #555 !important;
  }
  .footer {
    color: #aaa !important;
  }
}
</style>
